import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import CommonSelect from '../../Common/CommonSelect';
import CommonCheckbox from '../../Common/CommonCheckbox';
import { fetchSettings } from '../../../store/modules/item/itemActions';
import useFormSettings from '../../../hooks/useFormSettings';


import { setItemSettings } from '../../../store/modules/item/itemSlice';
import HandleQuickCreate from '../../Common/HandleQuickCreate';
import HandleQuickCreateWithChildren from '../../Common/HandleQuickCreateWithChildren';
import VatGroupForm from '../Vatgroups/VatGroupForm';
import { createVatGroup } from '../../../store/modules/vatgroup/vatgroupActions';
import PostingGroupForm from '../Postinggroups/PostingGroupForm';
import { createPostingGroup } from '../../../store/modules/postinggroup/postinggroupActions';
import MeasurementUnitForm from '../Measurementunits/MeasurementUnitForm';
import { createMeasurementUnit } from '../../../store/modules/measurementunit/measurementunitActions';
import ItemGroupForm from '../Itemgroups/ItemGroupForm';
import { createItemGroup } from '../../../store/modules/itemgroup/itemgroupActions';
import BrandForm from '../Brands/BrandForm';
import { createBrand } from '../../../store/modules/brand/brandActions';
import CategoryForm from '../Categories/CategoryForm';
import { createCategory } from '../../../store/modules/category/categoryActions';
import SubCategoryForm from '../Subcategories/SubCategoryForm';
import { createSubCategory } from '../../../store/modules/subcategory/subcategoryActions';
import { useHasPermission } from '../../../utils/permissionUtils';

const ItemForm = ({ initialData, onSave, isLoading, onClose }) => {

    const hasCreatePermission = useHasPermission(['create items']);
    const hasUpdatePermission = useHasPermission(['update items']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('item', fetchSettings);
    const { categories, item_groups, brands, measurement_units, posting_groups, item_types, vat_groups } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        item_code: '',
        name: {},
        part_no: '',
        invoice_description: {},
        category: null,
        sub_category: null,
        item_group: null,
        brand: null,
        measurement_unit: null,
        unit_price: '',
        unit_cost: '',
        posting_group: null,
        shelf_number: '',
        is_ladies_item: false,
        is_non_stock: false,
        item_type: null,
        vat_group: null,
        production_charge: '',
        is_active: false,
    });
        
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                ...initialData,
                name: initialData.name || {},
                invoice_description: initialData.invoice_description || {},
                category: initialData.category || null,
                sub_category: initialData.sub_category || null,
                item_group: initialData.item_group || null,
                brand: initialData.brand || null,
                measurement_unit: initialData.measurement_unit || null,
                unit_price: initialData.unit_price || '',
                unit_cost: initialData.unit_cost || '',
                posting_group: initialData.posting_group || null,
                shelf_number: initialData.shelf_number || '',
                is_ladies_item: initialData.is_ladies_item || false,
                is_non_stock: initialData.is_non_stock || false,
                item_type: initialData.item_type || null,
                vat_group: initialData.vat_group || null,
                production_charge: initialData.production_charge || '',
                is_active: initialData.is_active || false,
            }));
        }
    }, [initialData]);

    
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    }

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleCategoryChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            category: selectedOption,
            sub_category: null // Reset subcategory when category changes
        }));
        if (errors.category) {
            setErrors(prev => ({...prev, category: ''}));
        }
    };

    const handleSubCategoryChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            sub_category: selectedOption
        }));
        if (errors.sub_category) {
            setErrors(prev => ({...prev, sub_category: ''}));
        }
    };

    const handleSubCategoriesLoaded = (subCategories) => {
        // This function is called when subcategories are loaded
        // We don't need to do anything here as we want no default selection
    };


    const handleDelete = (value) => {
        console.log(value);
    }



    const handleVatGroupAddClick = HandleQuickCreate({
        title: 'Create Vat Group',
        FormComponent: VatGroupForm,
        createAction: createVatGroup,
        settingsKey: 'vat_groups',
        formDataKey: 'vat_group',
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings
    });
    const handlePostingGroupAddClick = HandleQuickCreate({
        title: 'Create Posting Group',
        FormComponent: PostingGroupForm,
        createAction: createPostingGroup,
        settingsKey: 'posting_groups',
        formDataKey: 'posting_group',
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings
    });
    const handleMeasurementUnitAddClick = HandleQuickCreate({
        title: 'Create Measurement Unit',
        FormComponent: MeasurementUnitForm,
        createAction: createMeasurementUnit,
        settingsKey: 'measurement_units',
        formDataKey: 'measurement_unit',
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings
    });

    const handleItemGroupAddClick = HandleQuickCreate({
        title: 'Create Item Group',
        FormComponent: ItemGroupForm,
        createAction: createItemGroup,
        settingsKey: 'item_groups',
        formDataKey: 'item_group',  
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings
    });

    const handleBrandAddClick = HandleQuickCreate({
        title: 'Create Brand',
        FormComponent: BrandForm,
        createAction: createBrand,
        settingsKey: 'brands',
        formDataKey: 'brand',
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings
    });
    
    const { handleCreate: handleCategoryCreate, showAddButton: showCategoryAddButton } = HandleQuickCreateWithChildren({
        title: 'Create Category',
        FormComponent: CategoryForm,
        createAction: createCategory,
        settingsKey: 'categories',
        formDataKey: 'category',
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings,
        resetKey: 'sub_category'
    });

    const { handleCreate: handleSubCategoryCreate, showAddButton: showSubCategoryAddButton } = HandleQuickCreateWithChildren({
        title: 'Create Sub Category',
        FormComponent: SubCategoryForm,
        createAction: createSubCategory,
        settingsKey: 'categories',
        formDataKey: 'sub_category',
        moduleName: 'item',
        setFormData,
        setSettingsAction: setItemSettings,
        parent: formData.category,
        parentKey: 'category'
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="item_code"
                            label="Item Code"
                            value={formData.item_code}
                            onChange={handleChange}
                            placeholder="Enter item code"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Item Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="part_no"
                            label="Item Part No"
                            value={formData.part_no}
                            onChange={handleChange}
                            placeholder="Enter part no"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Invoice Description"
                            name="invoice_description"
                            rows={1}
                            value={formData.invoice_description}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="category"
                            label="Category"
                            value={formData.category}
                            onChange={handleCategoryChange}
                            options={categories}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select category..."
                            hasSubOptions={true}
                            onSubOptionChange={handleSubCategoriesLoaded}
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleCategoryCreate()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="sub_category"
                            label="Sub Category"
                            value={formData.sub_category}
                            onChange={handleSubCategoryChange}
                            options={formData.category?.children || []}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select sub category..."
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleSubCategoryCreate()
                                }
                            ]}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4 d-none">
                        <CommonSelect
                            name="item_group"
                            label="Item Group"
                            value={formData.item_group}
                            onChange={(value) => handleSelectChange('item_group', value)}
                            options={item_groups}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select item group..."
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleItemGroupAddClick()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="brand"
                            label="Brand"
                            value={formData.brand}
                            onChange={(value) => handleSelectChange('brand', value)}
                            options={brands}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select brand..."
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleBrandAddClick()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="production_charge"
                            label="Production Charge"
                            value={formData.production_charge}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            placeholder="Enter production charge"
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="measurement_unit"
                            label="UOM"
                            value={formData.measurement_unit}
                            onChange={(value) => handleSelectChange('measurement_unit', value)}
                            options={measurement_units}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select UOM..."
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleMeasurementUnitAddClick()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="unit_price"
                            label="Unit Price"
                            value={formData.unit_price}
                            onChange={handleChange}
                            placeholder="Enter unit price"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="unit_cost"
                            label="Unit Cost"
                            value={formData.unit_cost}
                            onChange={handleChange}
                            placeholder="Enter unit cost"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="posting_group"
                            label="Item Posting Group"
                            value={formData.posting_group}
                            onChange={(value) => handleSelectChange('posting_group', value)}
                            options={posting_groups}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select item posting group..."
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handlePostingGroupAddClick()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="item_type"
                            label="Item Type"
                            value={formData.item_type}
                            onChange={(value) => handleSelectChange('item_type', value)}
                            options={item_types}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select item type..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="shelf_number"
                            label="Shelf Number"
                            value={formData.shelf_number}
                            onChange={handleChange}
                            placeholder="Enter Shelf Number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="vat_group"
                            label="VAT Group"
                            value={formData.vat_group}
                            onChange={(value) => handleSelectChange('vat_group', value)}
                            options={vat_groups}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select vat group..."
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleVatGroupAddClick()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonCheckbox
                            name="is_ladies_item"
                            label="Is Ladies Item?"
                            value={formData.is_ladies_item}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonCheckbox
                            name="is_non_stock"
                            label="Is Non Stock?"
                            value={formData.is_non_stock}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    id="is_active"
                                    disabled={isSubmitting}
                                />
                                <label className="form-check-label user-select-none" htmlFor="is_active">
                                    Active
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default ItemForm; 